/**
 * The reusable "choose an image" modal.
 *
 * This is the component every other module is meant to drop in — insurer
 * logos, product icons, microsite section artwork — rather than growing its own
 * upload widget. Two entry points:
 *
 *   * `<AssetPicker>`      — the modal, when you already have your own trigger;
 *   * `<AssetPickerField>` — a labelled form control with a thumbnail preview,
 *                            Change / Remove buttons and the modal wired up.
 *
 * What a consumer stores: the asset **id** for a real FK
 * (`models.ForeignKey("assets.MediaAsset", ...)`, which is what makes usage
 * tracking and delete-protection work), or `asset.stable_url` when the field is
 * only a URL string. Never `asset.url` — that one moves when the file is
 * replaced with a different format.
 */

import { useMemo, useState } from 'react';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { Badge, Button, EmptyState, Pagination } from '@/components/common';
import { cn } from '@/utils';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import {
  formatBytes,
  formatDimensions,
  type AssetKind,
  type AssetListParams,
  type MediaAsset,
} from '@/services/assets';
import { useAssetConfig, useAssetFolders, useAssets } from '@/hooks/useAssets';

import { AssetFilters } from './AssetFilters';
import { EMPTY_FILTERS, filtersToParams, type AssetFilterState } from './assetFilterState';
import { AssetGrid } from './AssetGrid';
import { AssetThumb } from './AssetThumb';
import { FolderNav, type FolderSelection } from './FolderNav';
import { MediaDialog } from './MediaDialog';
import { UploadDropzone } from './UploadDropzone';

const PAGE_SIZE = 24;

interface AssetPickerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the chosen asset (single mode). */
  onSelect?: (asset: MediaAsset) => void;
  /** Called with the chosen assets (multiple mode). */
  onSelectMany?: (assets: MediaAsset[]) => void;
  multiple?: boolean;
  title?: string;
  description?: string;
  /** Currently chosen id, highlighted in the grid. */
  value?: string | null;
  /** Restrict the choice, e.g. `kind="raster"` for a field a canvas will read. */
  kind?: AssetKind;
  /** Pre-select a folder. */
  initialFolder?: string | null;
}

export function AssetPicker({
  isOpen,
  onClose,
  onSelect,
  onSelectMany,
  multiple = false,
  title = 'Choose an image',
  description,
  value = null,
  kind,
  initialFolder = null,
}: AssetPickerProps) {
  const [tab, setTab] = useState<'library' | 'upload'>('library');
  const [page, setPage] = useState(1);
  const [folder, setFolder] = useState<FolderSelection>(initialFolder);
  const [filters, setFilters] = useState<AssetFilterState>({ ...EMPTY_FILTERS, kind: kind || '' });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [picked, setPicked] = useState<MediaAsset[]>([]);
  const [uploadBusy, setUploadBusy] = useState(false);

  const { term, debouncedTerm, setTerm } = useDebouncedSearch('');
  const config = useAssetConfig();
  const folders = useAssetFolders();

  const params: AssetListParams = useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      search: debouncedTerm || undefined,
      folder: folder === null ? undefined : folder,
      ...filtersToParams(filters),
      // A caller-imposed kind is not negotiable — it wins over the filter bar.
      kind: kind || filters.kind || undefined,
    }),
    [page, debouncedTerm, folder, filters, kind],
  );

  const assets = useAssets(params, isOpen);
  const results = assets.data?.results || [];

  const choose = (asset: MediaAsset) => {
    if (multiple) {
      setPicked((current) =>
        current.some((item) => item.id === asset.id)
          ? current.filter((item) => item.id !== asset.id)
          : [...current, asset],
      );
      return;
    }
    onSelect?.(asset);
    onClose();
  };

  const confirmMany = () => {
    onSelectMany?.(picked);
    setPicked([]);
    onClose();
  };

  return (
    <MediaDialog
      isOpen={isOpen}
      onClose={uploadBusy ? () => {} : onClose}
      size="6xl"
      title={title}
      description={description}
      closeOnOverlayClick={!uploadBusy}
      toolbar={
        <div className="flex gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-900/60">
          <TabButton active={tab === 'library'} onClick={() => setTab('library')}>
            Library
          </TabButton>
          <TabButton active={tab === 'upload'} onClick={() => setTab('upload')}>
            Upload new
          </TabButton>
        </div>
      }
      footer={
        multiple ? (
          <>
            <span className="mr-auto text-sm text-gray-500 dark:text-gray-400">
              {picked.length} selected
            </span>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={confirmMany} disabled={!picked.length}>
              Use {picked.length || ''} image{picked.length === 1 ? '' : 's'}
            </Button>
          </>
        ) : undefined
      }
    >
      {tab === 'upload' ? (
        <UploadDropzone
          folder={folder === 'none' ? null : folder}
          config={config.data}
          onBusyChange={setUploadBusy}
          // Deliberately `onBatchComplete`, not `onUploaded`: closing the modal
          // on the FIRST file would unmount the dropzone and abort every upload
          // still in flight, silently losing the rest of a multi-file drop.
          onBatchComplete={(uploaded) => {
            if (multiple) {
              setPicked((current) => [...current, ...uploaded]);
              setTab('library');
              return;
            }
            if (uploaded.length === 1) {
              onSelect?.(uploaded[0]);
              onClose();
              return;
            }
            // Several files for a single-image field: show them rather than
            // guessing which one was meant.
            setTab('library');
          }}
        />
      ) : (
        <div className="grid gap-5 md:grid-cols-[190px_minmax(0,1fr)]">
          <div className="hidden md:block">
            <FolderNav
              folders={folders.data || []}
              selected={folder}
              onSelect={(next) => {
                setFolder(next);
                setPage(1);
              }}
              totalCount={assets.data?.count}
              isLoading={folders.isLoading}
              readOnly
            />
          </div>

          <div className="space-y-4">
            <AssetFilters
              search={term}
              onSearchChange={(next) => {
                setTerm(next);
                setPage(1);
              }}
              filters={filters}
              onFiltersChange={(next) => {
                setFilters(next);
                setPage(1);
              }}
              config={config.data}
              showAdvanced={showAdvanced}
              onToggleAdvanced={() => setShowAdvanced((value) => !value)}
              compact
            />

            <div className="max-h-[52vh] overflow-y-auto pr-1">
              <AssetGrid
                assets={results}
                isLoading={assets.isLoading}
                onOpen={choose}
                activeId={value}
                selectedIds={multiple ? picked.map((item) => item.id) : undefined}
                onToggleSelect={multiple ? choose : undefined}
                compact
                columnsClassName="grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
                emptyState={
                  <EmptyState
                    icon={<PhotoIcon />}
                    title="No images match"
                    description="Try a different search, or upload a new image."
                    action={<Button onClick={() => setTab('upload')}>Upload new</Button>}
                  />
                }
              />
            </div>

            {(assets.data?.count || 0) > PAGE_SIZE && (
              <Pagination
                currentPage={page}
                totalPages={assets.data?.total_pages || 1}
                totalItems={assets.data?.count || 0}
                itemsPerPage={PAGE_SIZE}
                onPageChange={setPage}
              />
            )}
          </div>
        </div>
      )}
    </MediaDialog>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
        active
          ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
          : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white',
      )}
    >
      {children}
    </button>
  );
}

// ─── The form control other modules drop in ───────────────────────────────────

interface AssetPickerFieldProps {
  label?: string;
  hint?: string;
  /** The currently chosen asset, if the consumer has it loaded. */
  asset?: MediaAsset | null;
  /** Fallback preview when only a URL was stored, not a full asset. */
  fallbackUrl?: string | null;
  onChange: (asset: MediaAsset | null) => void;
  kind?: AssetKind;
  disabled?: boolean;
  error?: string;
  className?: string;
  /** Aspect of the preview box. Defaults to a wide logo slot. */
  previewClassName?: string;
}

export function AssetPickerField({
  label,
  hint,
  asset = null,
  fallbackUrl = null,
  onChange,
  kind,
  disabled = false,
  error,
  className,
  previewClassName = 'aspect-[16/9]',
}: AssetPickerFieldProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className={className}>
      {label && (
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
      )}

      <div
        className={cn(
          'overflow-hidden rounded-xl border',
          error ? 'border-red-300' : 'border-gray-200 dark:border-gray-700',
        )}
      >
        <div className={cn('w-full bg-gray-50 dark:bg-gray-900', previewClassName)}>
          {asset ? (
            <AssetThumb asset={asset} sizes="320px" />
          ) : fallbackUrl ? (
            <img src={fallbackUrl} alt="" className="h-full w-full object-contain" />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-1 text-gray-400">
              <PhotoIcon className="h-8 w-8" />
              <span className="text-xs">No image chosen</span>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
          {/* `type="button"` is load-bearing, not decoration (#204). The shared
              <Button> forwards to a bare <button>, whose HTML default type is
              "submit" -- and every consumer of this field renders it inside a
              <form>. Without this, "Choose image" submitted the surrounding
              form: on Edit Product that ran the save and closed the modal, so
              the picker looked like it simply never opened. */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setOpen(true)}
            disabled={disabled}
          >
            {asset || fallbackUrl ? 'Change image' : 'Choose image'}
          </Button>
          {(asset || fallbackUrl) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              leftIcon={<XMarkIcon className="h-4 w-4" />}
              onClick={() => onChange(null)}
              disabled={disabled}
            >
              Remove
            </Button>
          )}
          {asset && (
            <span className="ml-auto flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              {!asset.alt_text && (
                <Badge variant="warning" size="sm">
                  No alt text
                </Badge>
              )}
              {formatDimensions(asset)} · {formatBytes(asset.bytes)}
            </span>
          )}
        </div>
      </div>

      {error ? (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      ) : (
        hint && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      )}

      <AssetPicker
        isOpen={open}
        onClose={() => setOpen(false)}
        onSelect={(chosen) => onChange(chosen)}
        value={asset?.id || null}
        kind={kind}
        title={label ? `Choose an image for ${label.toLowerCase()}` : 'Choose an image'}
      />
    </div>
  );
}
